import { useWindowSize } from "hooks/useWindowSize";
import { Chip } from "components/DesignSystem/Chips/Chips";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { BooksDateFilter } from "components/Filters/BooksDateFilter";
import { LEDGER_TYPE_OPTIONS } from "constants/ledgerEntryTypes";
import { formatDate } from "utils/formatDate";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { PriceInput } from "components/PriceInput/PriceInput";
import { Form, Formik } from "formik";
import { ChangeEvent, useEffect } from "react";
import { debounce } from "utils/debouncing";
import { Button } from "components/DesignSystem/Button/Button";
import { Funnel } from "components/icons/Funnel";
import { getDatePeriod } from "utils/getDatePeriod";
import ConditionalToolTip from "components/design/conditionalToolTip";
import classNames from "classnames";
import { JOURNAL_ENTRY_STATUS } from "constants/journalEntrySources";

// Custom Amount Filter Component for Journal Entry
const JournalEntryAmountFilter = ({
  cashFlow,
  minAmount,
  maxAmount,
  updateFilter,
}: {
  cashFlow: string;
  minAmount: string;
  maxAmount: string;
  updateFilter: (name: string, value: any) => void;
}) => {
  const handleRadioCheck = (
    value: string,
    setValues: (values: { minAmount: string; maxAmount: string }) => void
  ) => {
    if (value === "") {
      setValues({ minAmount: "", maxAmount: "" });
      updateFilter("MIN_AMOUNT", "");
      updateFilter("MAX_AMOUNT", "");
      updateFilter("CASH_FLOW", "");
      return;
    }
    updateFilter("CASH_FLOW", value);
  };

  const handleAmountChange = debounce((e: ChangeEvent<HTMLFormElement>) => {
    const { value, name } = e.target;
    const upperName = name.toUpperCase();
    updateFilter(upperName, value);
  });

  return (
    <Formik
      initialValues={{
        minAmount: minAmount || "",
        maxAmount: maxAmount || "",
      }}
      onSubmit={() => {}}
    >
      {({ setValues }) => {
        return (
          <div className="t-flex t-flex-col t-gap-5">
            <div className="t-flex t-flex-col t-gap-3">
              <div className="t-text-body-sm t-text-neutral-80 t-w-full">
                Type
              </div>
              <RadioGroup.Root
                onValueChange={(value) => handleRadioCheck(value, setValues)}
                value={cashFlow || ""}
              >
                <RadioGroup.Content>
                  <RadioGroup.Item value="">Any</RadioGroup.Item>
                  <RadioGroup.Item value="credit">Credit only</RadioGroup.Item>
                  <RadioGroup.Item value="debit">Debit only</RadioGroup.Item>
                </RadioGroup.Content>
              </RadioGroup.Root>
            </div>
            <div className="t-flex t-flex-col t-gap-3">
              <div className="t-text-body-sm t-text-neutral-80 t-w-full">
                Amount range
              </div>
              <Form
                className="all:unset t-flex t-flex-col t-gap-4"
                onChange={handleAmountChange}
              >
                <PriceInput
                  name="minAmount"
                  label="Min. Amount"
                  disabled={Boolean(!cashFlow)}
                />
                <PriceInput
                  name="maxAmount"
                  label="Max. Amount"
                  disabled={Boolean(!cashFlow)}
                />
              </Form>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export const JournalEntryFilter = ({
  values,
  updateFilter,
}: {
  values: {
    START_DATE: string;
    END_DATE: string;
    SELECT_PERIOD: string;
    LEDGER_TYPES: string[];
    ACCOUNTING_METHOD: string[];
    SOURCE: string[];
    RECURRING_ENTRIES_ONLY: boolean;
    HIDE_FUTURE_ENTRIES: boolean;
    STATUS: string[];
  };
  updateFilter: <S extends keyof typeof values>(
    name: S,
    newValue: (typeof values)[S]
  ) => void;
}) => {
  const hasDateFilter = Boolean(values.START_DATE || values.END_DATE);
  const { isMobile } = useWindowSize();

  const appliedFilterCount = [
    values.SOURCE.length > 0,
    values.RECURRING_ENTRIES_ONLY,
    values.HIDE_FUTURE_ENTRIES && !hasDateFilter,
    values.STATUS.length > 0,
    hasDateFilter,
  ].filter(Boolean).length;

  let dateChipLabel = `Since ${formatDate(values.START_DATE)}`;
  if (values.START_DATE && values.END_DATE) {
    dateChipLabel = `${formatDate(values.START_DATE)} - ${formatDate(
      values.END_DATE
    )}`;
  }
  if (!values.START_DATE) {
    dateChipLabel = `Till ${formatDate(values.END_DATE)}`;
  }

  const handleDateChange = <
    S extends "START_DATE" | "END_DATE" | "SELECT_PERIOD"
  >(
    name: S,
    newValue: { START_DATE: string; END_DATE: string; SELECT_PERIOD: string }[S]
  ) => {
    updateFilter("HIDE_FUTURE_ENTRIES", false);
    updateFilter(name, newValue);
  };

  return (
    <Filter.Root
      withWrapper={false}
      defaultValue="journalEntryDate"
      trigger={
        <Button
          customType="icon"
          size="xsmall"
          className="sm:!t-w-auto sm:!t-px-2"
        >
          <Funnel />
          {isMobile && appliedFilterCount > 0 && (
            <span className="t-text-text-30 t-ml-1">
              ({appliedFilterCount})
            </span>
          )}
        </Button>
      }
      capsule={
        <span className="t-contents sm:t-hidden">
          <Chip
            isActive={values.SOURCE.includes("MANUAL")}
            onClick={() =>
              updateFilter(
                "SOURCE",
                values.SOURCE.includes("MANUAL")
                  ? values.SOURCE.filter((s) => s !== "MANUAL")
                  : [...values.SOURCE, "MANUAL"]
              )
            }
            onRemove={() =>
              updateFilter(
                "SOURCE",
                values.SOURCE.filter((s) => s !== "MANUAL")
              )
            }
            filterType="source"
            isFixedFilter
          >
            Manually added
          </Chip>{" "}
          {values.SOURCE.includes("SYSTEM") && (
            <Chip
              isActive
              filterType="source"
              onRemove={() => {
                updateFilter(
                  "SOURCE",
                  values.SOURCE.filter((s) => s !== "SYSTEM")
                );
              }}
            >
              System generated
            </Chip>
          )}
          <Chip
            isActive={values.RECURRING_ENTRIES_ONLY}
            onRemove={() => updateFilter("RECURRING_ENTRIES_ONLY", false)}
            onClick={() =>
              updateFilter(
                "RECURRING_ENTRIES_ONLY",
                !values.RECURRING_ENTRIES_ONLY
              )
            }
            filterType="others"
            isFixedFilter
          >
            Recurring
          </Chip>
          <Chip
            isActive={values.HIDE_FUTURE_ENTRIES && !hasDateFilter}
            onRemove={() => updateFilter("HIDE_FUTURE_ENTRIES", false)}
            onClick={() => {
              if (hasDateFilter) return;
              updateFilter("HIDE_FUTURE_ENTRIES", !values.HIDE_FUTURE_ENTRIES);
            }}
            filterType="others"
            isFixedFilter
            disabled={hasDateFilter}
          >
            <ConditionalToolTip
              condition={
                values.HIDE_FUTURE_ENTRIES &&
                !hasDateFilter && (
                  <div className="t-w-60">
                    Remove this filter to view journal entries scheduled for
                    future dates.
                  </div>
                )
              }
              align="start"
              sideOffset={10}
              side="top"
            >
              <span
                className={classNames({ "t-text-neutral-40": hasDateFilter })}
              >
                Hide future entries
              </span>
            </ConditionalToolTip>
          </Chip>
          <Chip
            isActive={values.STATUS.includes(JOURNAL_ENTRY_STATUS.ACTIVE)}
            onClick={() =>
              updateFilter(
                "STATUS",
                values.STATUS.includes(JOURNAL_ENTRY_STATUS.ACTIVE)
                  ? values.STATUS.filter(
                      (s) => s !== JOURNAL_ENTRY_STATUS.ACTIVE
                    )
                  : [...values.STATUS, JOURNAL_ENTRY_STATUS.ACTIVE]
              )
            }
            onRemove={() =>
              updateFilter(
                "STATUS",
                values.STATUS.filter((s) => s !== JOURNAL_ENTRY_STATUS.ACTIVE)
              )
            }
            filterType="status"
            isFixedFilter
          >
            Active
          </Chip>{" "}
          {values.STATUS.includes(JOURNAL_ENTRY_STATUS.ARCHIVED) && (
            <Chip
              isActive
              filterType="status"
              onRemove={() => {
                updateFilter(
                  "STATUS",
                  values.STATUS.filter(
                    (s) => s !== JOURNAL_ENTRY_STATUS.ARCHIVED
                  )
                );
              }}
            >
              Archived entries
            </Chip>
          )}
          {(values.START_DATE || values.END_DATE) && (
            <Chip
              onRemove={() => {
                updateFilter("START_DATE", "");
                updateFilter("END_DATE", "");
                updateFilter("HIDE_FUTURE_ENTRIES", true);
              }}
              isActive
              filterType="journalEntryDate"
            >
              {dateChipLabel}
            </Chip>
          )}
          {values.LEDGER_TYPES.length > 0 && (
            <Chip
              onRemove={() => updateFilter("LEDGER_TYPES", [])}
              isActive
              filterType="ledgerTypes"
            >
              Types ({values.LEDGER_TYPES.length})
            </Chip>
          )}
          {values.ACCOUNTING_METHOD.length > 0 && (
            <Chip
              onRemove={() => updateFilter("ACCOUNTING_METHOD", [])}
              isActive
              filterType="basis"
            >
              Basis ({values.ACCOUNTING_METHOD.length})
            </Chip>
          )}
        </span>
      }
    >
      <Filter.Portal>
        <Filter.List>
          <Filter.ListItem value="journalEntryDate">Date</Filter.ListItem>
          <Filter.ListItem value="ledgerTypes">Type</Filter.ListItem>
          <Filter.ListItem value="basis">Basis</Filter.ListItem>
          <Filter.ListItem value="source">Source</Filter.ListItem>
          <Filter.ListItem value="status">Status</Filter.ListItem>
          <Filter.ListItem value="others">Others</Filter.ListItem>
        </Filter.List>
        <Filter.Body value="journalEntryDate" block>
          <BooksDateFilter
            datePeriod={getDatePeriod}
            values={values}
            updateFilter={handleDateChange}
            skipMaxEndDate
          />
        </Filter.Body>
        <Filter.Body value="ledgerTypes" block>
          <MultiSelectFilter
            onChange={(selectedTypes) =>
              updateFilter("LEDGER_TYPES", selectedTypes)
            }
            options={LEDGER_TYPE_OPTIONS}
            selected={values.LEDGER_TYPES}
            isSelectAll={false}
            isSearchable={false}
          />
        </Filter.Body>
        <Filter.Body value="basis" block>
          <MultiSelectFilter
            onChange={(selectedTypes) =>
              updateFilter("ACCOUNTING_METHOD", selectedTypes)
            }
            options={[
              {
                label: "Cash",
                value: "CASH",
              },
              {
                label: "Accrual",
                value: "ACCRUAL",
              },
            ]}
            selected={values.ACCOUNTING_METHOD}
            isSelectAll={false}
            isSearchable={false}
          />
        </Filter.Body>
        <Filter.Body value="source" block>
          <MultiSelectFilter
            onChange={(selectedTypes) => updateFilter("SOURCE", selectedTypes)}
            options={[
              {
                label: "Manual",
                value: "MANUAL",
              },
              {
                label: "System",
                value: "SYSTEM",
              },
            ]}
            selected={values.SOURCE}
            isSelectAll={false}
            isSearchable={false}
          />
        </Filter.Body>
        <Filter.Body value="status" block>
          <MultiSelectFilter
            onChange={(selectedTypes) => updateFilter("STATUS", selectedTypes)}
            options={[
              {
                label: "Active",
                value: JOURNAL_ENTRY_STATUS.ACTIVE,
              },
              {
                label: "Archived",
                value: JOURNAL_ENTRY_STATUS.ARCHIVED,
              },
            ]}
            selected={values.STATUS}
            isSelectAll={false}
            isSearchable={false}
          />
        </Filter.Body>
        <Filter.Body value="others" block>
          <div className="t-flex t-flex-col t-gap-3">
            <Checkbox
              name="RECURRING_ENTRIES_ONLY"
              checked={values.RECURRING_ENTRIES_ONLY}
              label="Recurring entries only"
              onChange={(e) =>
                updateFilter("RECURRING_ENTRIES_ONLY", e.target.checked)
              }
            />
            <Checkbox
              name="HIDE_FUTURE_ENTRIES"
              checked={values.HIDE_FUTURE_ENTRIES}
              label="Hide future entries"
              onChange={(e) =>
                updateFilter("HIDE_FUTURE_ENTRIES", e.target.checked)
              }
            />
          </div>
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};
